import type { ReactElement } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { AnyNullableCustomComponent } from '@flows/core/types/CustomComponent'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type {
  AccordionItem as AccordionItemProps,
  AccordionList as AccordionListProps,
} from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import Icon from '@ui-kit/components/SvgIcon'
import UiKitAccordionList from '@ui-kit/components/listComponents/AccordionList'
import type { AccordionItem } from '@ui-kit/components/listComponents/AccordionList/types'

type Props<
  TCustomComponent extends AnyNullableCustomComponent,
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> = Omit<
  AccordionListProps<AnyNullableCustomComponent, AnyCallbackConfig, AnySimpleOnSubmitHook>,
  'data'
> & {
  data: (Omit<
    AccordionItemProps<TCustomComponent, TCallbackConfig, TSimpleOnSubmitHook>,
    'content' | 'headingRightContent'
  > & {
    content?: ReactElement
    headingRightContent?: ReactElement
  })[]
}
export const AccordionList = <TProps extends Props<any, any, any>>(
  props: TProps & { flowValues: FlowValues }
) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const textProcessor = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const data = props.data.map(
    (element): AccordionItem => ({
      ...element,
      headingRightContent: element.headingRightContent,
      content: element.content,
      // @ts-expect-error we can pass any string as an icon
      icon: element.icon ? <Icon imgSrc={element.icon} /> : undefined,
      title: textProcessor(element.title),
      subtitle: element.subtitle ? textProcessor(element.subtitle) : undefined,
    })
  )

  return <UiKitAccordionList data={data} />
}
